import { z } from "zod";
import type { BisonEnvelope } from "./bison.ts";
import { quoteOccurs } from "./quote-span.ts";
import type { ContextData } from "./stage-inputs.ts";
import type { ContextAnalysis } from "./stage-schemas.ts";

const sourceId = z.string().trim().min(1).max(256);
const MAX_VOICE_SAMPLES = 8;
export const MAX_VOICE_SAMPLE_CHARS = 1200;
const MAX_VOICE_THREAD_EXCERPTS = 16;
export const VoiceTraitsSchema = z.strictObject({
  register: z.enum(["formal", "neutral", "peer"]),
  warmth: z.enum(["reserved", "balanced", "warm"]),
  directness: z.enum(["gentle", "balanced", "direct"]),
});
const VoiceRiskSchema = z.strictObject({
  kind: z.enum([
    "template_leak",
    "encoding_artifact",
    "generation_meta",
    "repetitive_pitch",
    "identity_conflict",
    "unsupported_claim",
    "inconsistent_voice",
  ]),
  evidence_id: sourceId,
  quote: z.string().trim().min(1).max(1200),
});
export const VoiceAssessmentSchema = z.strictObject({
  outbound_quality: z.enum(["usable", "mixed", "unusable", "insufficient"]),
  risks: z.array(VoiceRiskSchema).max(24),
  source_ids: z.array(sourceId).max(MAX_VOICE_SAMPLES),
  proposed_voice: VoiceTraitsSchema,
  confidence: z.enum(["low", "medium", "high"]),
});
const traitSource = z.enum([
  "rep_confirmed",
  "latest_inbound",
  "campaign_samples",
  "neutral_fallback",
]);
export const AuthoredVoiceExcerptSchema = z.strictObject({
  text: z.string().max(MAX_VOICE_SAMPLE_CHARS),
  source_start: z.number().int().nonnegative(),
  source_end: z.number().int().nonnegative(),
  segmentation: z.enum(["markers_removed", "no_markers_found", "ambiguous"]),
  truncated: z.boolean(),
});
export const VoiceContextSchema = z.strictObject({
  mode: z.enum(["approved_preferences", "observed_campaign", "neutral_fallback"]),
  selected_traits: VoiceTraitsSchema,
  trait_sources: z.strictObject({
    register: traitSource,
    warmth: traitSource,
    directness: traitSource,
  }),
  confidence: z.enum(["low", "medium", "high"]),
  outbound_quality: z.enum(["usable", "mixed", "unusable", "insufficient"]),
  authorship: z.literal("unknown"),
  facts_authority: z.literal("none"),
  source_ids: z.array(sourceId).max(MAX_VOICE_SAMPLES),
  // Historical field name: distinct normalized texts, not independently authored messages.
  independent_sample_count: z.number().int().min(0).max(MAX_VOICE_SAMPLES),
  samples: z
    .array(
      z.strictObject({
        evidence_id: sourceId,
        text: z.string().max(MAX_VOICE_SAMPLE_CHARS),
        timestamp: z.string(),
      }),
    )
    .max(MAX_VOICE_SAMPLES),
  excluded_sources: z.array(z.strictObject({ evidence_id: sourceId, reason: z.string() })),
  verified_risks: z.array(VoiceRiskSchema),
  authored_thread: z
    .array(
      AuthoredVoiceExcerptSchema.extend({
        evidence_id: sourceId,
        direction: z.enum(["inbound", "outbound"]),
      }),
    )
    .max(MAX_VOICE_THREAD_EXCERPTS),
  approved_preferences: z.json().nullable(),
  warnings: z.array(z.string()),
});
export type VoiceContext = z.infer<typeof VoiceContextSchema>;
export type VoiceAssessment = z.infer<typeof VoiceAssessmentSchema>;
type VoiceTraits = z.infer<typeof VoiceTraitsSchema>;
type VoiceRisk = z.infer<typeof VoiceRiskSchema>;
const neutralTraits: VoiceTraits = {
  register: "neutral",
  warmth: "balanced",
  directness: "balanced",
};
const localizedReplyMarker =
  /(?:On .{1,200}wrote:|Le .{1,200}(?:a écrit|a ecrit)\s*:|Am .{1,200}schrieb.{0,100}:|El .{1,200}escribi[oó]\s*:|(?:Dnia|W dniu) .{1,200}napisa[łl](?:a)?\s*:|Em .{1,200}escreveu\s*:)/i;

/** A conservative contiguous source slice. No markers is not proof of authorship. */
export function authoredVoiceExcerpt(text: string): z.infer<typeof AuthoredVoiceExcerptSchema> {
  let cutoff = text.length;
  let foundMarker = false;
  let authoredLines = 0;
  for (const match of text.matchAll(/[^\r\n]*(?:\r?\n|$)/g)) {
    const line = match[0].replace(/[\r\n]+$/, "");
    const tail = text.slice(match.index + match[0].length);
    const localizedHeader = line.trimStart().match(localizedReplyMarker);
    const replyMarker =
      /^\s*(?:>|&gt;|From:\s|Sent:\s|De:\s|Envoyé\s*:|Von:\s|Gesendet\s*:|Od:\s|Wysłano\s*:|[-_]{2,}\s*(?:Original Message|Forwarded message))/i.test(
        line,
      ) || localizedHeader?.index === 0;
    const explicitSignature =
      /^\s*(?:--\s*$|Sent from my (?:iPhone|iPad|Android)|Get Outlook for)/i.test(line);
    const closingSignature =
      authoredLines > 0 &&
      /^\s*(?:Best(?: regards)?|Kind regards|Regards|Sincerely|Cheers|Thanks(?: again)?|Thank you|Merci|Cordialement|Viele Grüße|Mit freundlichen Grüßen|Saludos|Pozdrawiam)[,!]?\s*$/i.test(
        line,
      ) &&
      tail.split(/\r?\n/).length <= 7;
    if (replyMarker || explicitSignature || closingSignature) {
      cutoff = match.index;
      foundMarker = true;
      break;
    }
    if (line.trim()) authoredLines++;
  }
  const raw = text.slice(0, cutoff);
  const trimmed = raw.trim();
  const start = trimmed ? raw.indexOf(trimmed) : 0;
  const bounded = trimmed.slice(0, MAX_VOICE_SAMPLE_CHARS);
  const ambiguousMarker =
    localizedReplyMarker.test(trimmed) ||
    /[-_]{2,}\s*(?:Original Message|Forwarded message)/i.test(trimmed);
  return {
    text: bounded,
    source_start: start,
    source_end: start + bounded.length,
    segmentation: foundMarker
      ? "markers_removed"
      : ambiguousMarker
        ? "ambiguous"
        : "no_markers_found",
    truncated: bounded.length < trimmed.length,
  };
}
/** Observable corruption only. This is deliberately not an AI-authorship detector. */
export function voiceHygieneRisks(text: string, evidenceId: string): VoiceRisk[] {
  const scanners: readonly { kind: VoiceRisk["kind"]; pattern: RegExp }[] = [
    {
      kind: "template_leak",
      pattern:
        /\{\{[^{}\n]{1,160}\}\}|\{(?:EMAIL[ _]BODY|FIRST[ _]?NAME|LAST[ _]?NAME|COMPANY[ _]?NAME)\}|\[(?:INSERT|YOUR|FIRST NAME|EMAIL BODY)[^\]\n]{0,120}\]/gi,
    },
    {
      kind: "generation_meta",
      pattern:
        /(?:^|\n)\s*(?:Here(?: is|['’]s) (?:the|your) (?:draft|email|reply)|(?:SYSTEM|ASSISTANT|USER)\s*:|<\/?(?:system|assistant|analysis|final)>|```(?:json|xml)?\s*$)/gim,
    },
    {
      kind: "generation_meta",
      pattern: /(?:^|\n)\s*\{\s*"(?:draft_body|email_body|subject|body|reasoning)"\s*:/gim,
    },
    {
      kind: "encoding_artifact",
      pattern:
        /\uFFFD|Ã[\u0080-\u00BF]|Â[\u0080-\u00BF]|â€[˜™œ“”¦–—]|&amp;(?:lt|gt|amp);|(?:&#(?:x[0-9a-f]{2,6}|\d{2,7});){2,}/gi,
    },
  ];
  return scanners
    .flatMap(({ kind, pattern }) =>
      [...text.matchAll(pattern)]
        .slice(0, 3)
        .map((match) => ({ kind, evidence_id: evidenceId, quote: match[0].trim().slice(0, 1200) })),
    )
    .filter((risk) => risk.quote.length > 0);
}
function independentText(text: string): string {
  return text.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
}

/** Build a bounded style-only view; damaged or missing voice evidence never blocks drafting. */
export function buildVoiceContext(
  input: BisonEnvelope,
  context: ContextData,
  analysis: ContextAnalysis | null,
): VoiceContext {
  const assessment = analysis?.voice_assessment;
  const warnings: string[] = [];
  const excluded: VoiceContext["excluded_sources"] = [];
  const verifiedRisks: VoiceRisk[] = [];
  const messages = new Map<string, { text: string; timestamp: string }>();
  const seenIds = new Set<string>();
  const duplicateIds = new Set<string>();
  const clock = Date.parse(input.cutoff ?? input.retrieved_at);
  for (const message of input.messages) {
    if (seenIds.has(message.id)) duplicateIds.add(message.id);
    seenIds.add(message.id);
    if (message.direction !== "outbound") continue;
    if (
      !message.sources.length ||
      message.sources.some((source) => source.workspace_id !== input.workspace.id)
    ) {
      excluded.push({ evidence_id: message.id, reason: "Unverified workspace scope" });
      continue;
    }
    // The "newer than clock" half is exercised directly at this function's boundary — see
    // tests/voice.test.ts's "future" case — but is unreachable through a full `runPipeline` run
    // with a valid, cutoff-bearing envelope: bison.ts's envelope refinement already refuses to
    // parse any envelope whose messages are newer than `cutoff`, so a pipeline-level fixture and
    // this half of the branch are mutually exclusive whenever `clock` came from `input.cutoff`.
    // It stays reachable end-to-end only through the `retrieved_at` fallback (no `cutoff`), which
    // bison.ts does not similarly bound. An earlier draft of a pipeline-level regression test for
    // this asserted it vacuously (wrong field name), then permanently red; that test was removed
    // rather than built out into a real `retrieved_at`-fallback fixture. See tests/pipeline.test.ts
    // and tests/voice-pipeline.test.ts for the pipeline-level "four notions of now" coverage.
    if (
      !Number.isFinite(Date.parse(message.date_received)) ||
      Date.parse(message.date_received) > clock
    ) {
      excluded.push({ evidence_id: message.id, reason: "Outside the run evidence cutoff" });
      continue;
    }
    const excerpt = authoredVoiceExcerpt(message.text);
    const text = excerpt.text;
    if (excerpt.segmentation === "ambiguous") {
      excluded.push({
        evidence_id: message.id,
        reason: "Ambiguous quote boundary is not reliable voice evidence",
      });
      continue;
    }
    if (!text) {
      excluded.push({
        evidence_id: message.id,
        reason: "No authored text remains after removing quoted history",
      });
      continue;
    }
    messages.set(message.id, { text, timestamp: message.date_received });
    const risks = voiceHygieneRisks(text, message.id);
    verifiedRisks.push(...risks);
    if (risks.length)
      excluded.push({
        evidence_id: message.id,
        reason: "Observable template, generation, or encoding damage",
      });
  }
  for (const id of duplicateIds) {
    messages.delete(id);
    excluded.push({ evidence_id: id, reason: "Ambiguous duplicated message ID" });
  }
  let invalidAssessment = false;
  if (assessment) {
    for (const risk of assessment.risks) {
      const message = messages.get(risk.evidence_id);
      if (!message || !quoteOccurs(message.text, risk.quote)) {
        invalidAssessment = true;
        warnings.push(
          "An unverified voice-risk quote was discarded; campaign style is not trusted for this run.",
        );
        continue;
      }
      verifiedRisks.push(risk);
      excluded.push({
        evidence_id: risk.evidence_id,
        reason: `Source-quoted analyst risk: ${risk.kind}`,
      });
    }
    for (const id of assessment.source_ids)
      if (!messages.has(id)) {
        invalidAssessment = true;
        warnings.push(
          "A proposed voice source is not a scoped authored outbound message in this run.",
        );
      }
    if (new Set(assessment.source_ids).size !== assessment.source_ids.length) {
      invalidAssessment = true;
      warnings.push("Duplicate voice source IDs do not count as independent evidence.");
    }
  } else
    warnings.push(
      "No quality-aware campaign voice assessment is available; use a neutral baseline.",
    );
  const excludedIds = new Set(excluded.map((source) => source.evidence_id));
  const candidates = (assessment?.source_ids ?? [])
    .flatMap((id) => {
      const message = messages.get(id);
      return message && !excludedIds.has(id) ? [{ evidence_id: id, ...message }] : [];
    })
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  const independent = new Set<string>();
  const samples: VoiceContext["samples"] = [];
  for (const sample of candidates) {
    const key = independentText(sample.text);
    if (independent.has(key)) {
      excluded.push({
        evidence_id: sample.evidence_id,
        reason: "Duplicate authored text is not independent voice evidence",
      });
      continue;
    }
    independent.add(key);
    if (samples.length < MAX_VOICE_SAMPLES)
      samples.push({ ...sample, text: sample.text.slice(0, MAX_VOICE_SAMPLE_CHARS) });
  }
  const usable =
    assessment &&
    !invalidAssessment &&
    ["usable", "mixed"].includes(assessment.outbound_quality) &&
    samples.length > 0;
  let selected: VoiceTraits = usable ? { ...assessment.proposed_voice } : { ...neutralTraits };
  const baseline = usable ? "campaign_samples" : "neutral_fallback";
  const sources: VoiceContext["trait_sources"] = {
    register: baseline,
    warmth: baseline,
    directness: baseline,
  };
  let confidence: VoiceContext["confidence"] = usable ? assessment.confidence : "low";
  if (confidence === "high") {
    confidence = "medium";
    warnings.push(
      "Campaign observations are capped at medium confidence: distinct text does not prove independent or human authorship.",
    );
  }
  const latestInbound = input.messages.find(
    (message) =>
      message.direction === "inbound" &&
      message.provider_id === input.trigger.id &&
      !duplicateIds.has(message.id) &&
      Date.parse(message.date_received) <= clock &&
      message.sources.length > 0 &&
      message.sources.every((source) => source.workspace_id === input.workspace.id),
  );
  const inboundExcerpt = latestInbound ? authoredVoiceExcerpt(latestInbound.text) : null;
  const hasInboundVoice =
    inboundExcerpt && inboundExcerpt.text.length > 0 && inboundExcerpt.segmentation !== "ambiguous";
  if (analysis && hasInboundVoice) {
    selected.register = analysis.fact_plan.register;
    sources.register = "latest_inbound";
  }
  const approved = context.tone_preferences ?? null;
  // Arbitrary approved prose stays a bounded style-only datum. Only recognized finite
  // structured dimensions affect deterministic selection; no lexical guessing of intent.
  const structuredPreferences = z
    .object({
      register: VoiceTraitsSchema.shape.register.optional(),
      warmth: VoiceTraitsSchema.shape.warmth.optional(),
      directness: VoiceTraitsSchema.shape.directness.optional(),
    })
    .safeParse(approved);
  if (structuredPreferences.success) {
    const preferences = structuredPreferences.data;
    if (preferences.register) {
      selected.register = preferences.register;
      sources.register = "rep_confirmed";
    }
    if (preferences.warmth) {
      selected.warmth = preferences.warmth;
      sources.warmth = "rep_confirmed";
    }
    if (preferences.directness) {
      selected.directness = preferences.directness;
      sources.directness = "rep_confirmed";
    }
  }
  // The formal-lead guard remains stronger than a preference for casual campaign copy.
  if (hasInboundVoice && analysis?.fact_plan.register === "formal") {
    selected.register = "formal";
    sources.register = "latest_inbound";
  }
  selected = VoiceTraitsSchema.parse(selected);
  if (verifiedRisks.length)
    warnings.push(
      "Damaged or disputed outbound samples were excluded. Do not mimic their artifacts, claims, or pitch repetition.",
    );
  if (!usable && assessment)
    warnings.push(
      "Campaign style is unavailable or unreliable; neutral style does not require a rep clarification.",
    );
  if (verifiedRisks.some((risk) => risk.kind === "identity_conflict"))
    warnings.push(
      "Resolve seller identity separately through required context questions; voice selection grants no identity authority.",
    );
  const outputSamples = usable ? samples : [];
  const threadMessages = input.messages
    .filter(
      (message) =>
        !duplicateIds.has(message.id) &&
        message.sources.length > 0 &&
        message.sources.every((source) => source.workspace_id === input.workspace.id) &&
        Date.parse(message.date_received) <= clock,
    )
    .sort((a, b) => Date.parse(b.date_received) - Date.parse(a.date_received));
  const trigger = threadMessages.find(
    (message) => message.provider_id === input.trigger.id && message.direction === "inbound",
  );
  const selectedThread = (
    trigger
      ? [trigger, ...threadMessages.filter((message) => message.id !== trigger.id)]
      : threadMessages
  ).slice(0, MAX_VOICE_THREAD_EXCERPTS);
  const authoredThread = selectedThread.map((message) => ({
    evidence_id: message.id,
    direction: message.direction,
    ...authoredVoiceExcerpt(message.text),
  }));
  if (authoredThread.some((excerpt) => excerpt.segmentation !== "markers_removed"))
    warnings.push(
      "Unmarked excerpts may still contain quoted history or signatures. Segmentation is conservative evidence preparation, not proof of authorship.",
    );
  return VoiceContextSchema.parse({
    mode:
      approved !== null
        ? "approved_preferences"
        : usable
          ? "observed_campaign"
          : "neutral_fallback",
    selected_traits: selected,
    trait_sources: sources,
    confidence,
    outbound_quality: !assessment
      ? "insufficient"
      : invalidAssessment || (!samples.length && assessment.outbound_quality !== "insufficient")
        ? "unusable"
        : usable && verifiedRisks.length > 0
          ? "mixed"
          : assessment.outbound_quality,
    authorship: "unknown",
    facts_authority: "none",
    source_ids: outputSamples.map((sample) => sample.evidence_id),
    independent_sample_count: outputSamples.length,
    samples: outputSamples,
    excluded_sources: excluded,
    verified_risks: verifiedRisks,
    authored_thread: authoredThread,
    approved_preferences: approved,
    warnings: [...new Set(warnings)],
  });
}
